import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/supabase/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'
import { CreditCard, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react'
import { Helmet } from 'react-helmet-async'

// 어드민 유저 ID
const ADMIN_USER_IDS = ['bd873ddc-9c11-4093-a4e6-a2fb74e2cb71']

interface PastDueRow {
  subscription_id: number
  user_id: string
  nickname: string | null
  subscription_plan_id: string
  plan_name: string | null
  amount: number | null
  period_start: string
  period_end: string
  has_billing_key: boolean
}

// 행 상태: 재결제 결과에 따른 UI 상태
type RowStatus =
  | { kind: 'idle' }
  | { kind: 'retrying' }
  | { kind: 'declined'; errorType?: string; message?: string; pgMessage?: string }
  | { kind: 'precondition_failed'; reason: string }

export default function AdminBillingRetryPage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [rows, setRows] = useState<PastDueRow[]>([])
  const [rowStatus, setRowStatus] = useState<Record<number, RowStatus>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // 어드민 체크
  useEffect(() => {
    if (user && !ADMIN_USER_IDS.includes(user.id)) {
      toast.error('접근 권한이 없습니다.')
      navigate('/')
    }
  }, [user, navigate])

  // 데이터 로드
  useEffect(() => {
    if (!user || !ADMIN_USER_IDS.includes(user.id)) return

    const loadData = async () => {
      setIsLoading(true)
      setLoadError(null)
      try {
        const { data, error } = await supabase.functions.invoke('admin-billing-list-past-due')
        if (error) throw error
        if (!data?.success) {
          throw new Error(data?.message || '목록 조회 실패')
        }
        setRows((data.items ?? []) as PastDueRow[])
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        console.error('[AdminBillingRetry] 목록 조회 오류:', err)
        setLoadError(msg)
        toast.error('목록을 불러오지 못했습니다.')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [user])

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
  }

  const formatAmount = (n: number | null) => (n == null ? '-' : `₩${n.toLocaleString()}`)

  // 재결제 처리 — IDEMPOTENCY WARNING 계약 그대로 반영
  const handleRetry = async (row: PastDueRow) => {
    const current = rowStatus[row.subscription_id]
    if (current?.kind === 'retrying') return

    if (!row.has_billing_key) {
      toast.error('빌링키가 없는 유저입니다. 재결제 불가.')
      return
    }

    const ok = window.confirm(
      `정말 재결제를 시도하시겠습니까?\n\n` +
        `유저: ${row.nickname ?? row.user_id}\n` +
        `플랜: ${row.plan_name ?? row.subscription_plan_id}\n` +
        `금액: ${formatAmount(row.amount)}\n\n` +
        `※ PortOne으로 실제 결제가 발생합니다.`,
    )
    if (!ok) return

    setRowStatus((prev) => ({ ...prev, [row.subscription_id]: { kind: 'retrying' } }))

    try {
      // subscription_id를 함께 보내 클릭한 행과 실제 청구 행을 일치시킨다
      const { data, error } = await supabase.functions.invoke('admin-billing-retry', {
        body: { user_id: row.user_id, subscription_id: row.subscription_id },
      })

      // supabase.functions.invoke는 200번대만 success로 보고, 4xx/5xx는 error에 담는다.
      // 다만 우리 함수는 결제 거절을 200으로 응답하므로 그 케이스는 data로 들어옴.
      if (error) {
        // 5xx 케이스: data가 있을 수도 있지만 invoke 헬퍼는 보장 안 함.
        // FunctionsHttpError의 context.json()으로 body를 꺼낼 수 있지만 여기선 단순화.
        // warning/infra 케이스는 아래 try에서 처리하도록 fetch fallback.
        throw error
      }

      // ── 200 응답 분기 ──
      if (data?.success === true) {
        toast.success(`재결제 성공! (₩${(data.amount ?? row.amount ?? 0).toLocaleString()})`)
        setRows((prev) => prev.filter((r) => r.subscription_id !== row.subscription_id))
        setRowStatus((prev) => {
          const next = { ...prev }
          delete next[row.subscription_id]
          return next
        })
        return
      }

      const reason = data?.reason as string | undefined

      if (reason === 'payment_declined') {
        toast.error(
          `결제 거절: ${data?.pgMessage || data?.message || data?.error_type || '알 수 없음'}`,
          { duration: 6000 },
        )
        setRowStatus((prev) => ({
          ...prev,
          [row.subscription_id]: {
            kind: 'declined',
            errorType: data?.error_type,
            message: data?.message,
            pgMessage: data?.pgMessage,
          },
        }))
        return
      }

      if (
        reason === 'no_past_due_subscription' ||
        reason === 'no_billing_key' ||
        reason === 'no_plan_price'
      ) {
        toast.error(`재결제 불가: ${reason}`)
        setRowStatus((prev) => ({
          ...prev,
          [row.subscription_id]: { kind: 'precondition_failed', reason },
        }))
        return
      }

      // 클릭한 행이 서버 canonical 행과 다름 (stale 목록) — 결제는 발생하지 않았음
      if (reason === 'subscription_mismatch') {
        toast.error('목록이 최신이 아닙니다. 목록을 다시 불러옵니다.')
        window.setTimeout(() => window.location.reload(), 1500)
        return
      }

      // 유저 재결제와 동시 실행 등으로 클레임 선점 실패 — 결제는 발생하지 않았음
      if (reason === 'retry_in_progress') {
        toast.info('다른 재결제가 진행 중입니다. 잠시 후 목록을 새로고침해 확인하세요.')
        setRowStatus((prev) => {
          const next = { ...prev }
          delete next[row.subscription_id]
          return next
        })
        return
      }

      // 그 외 200 응답 — 예상치 못한 케이스
      toast.error(`알 수 없는 응답: ${reason ?? 'no_reason'}`)
      setRowStatus((prev) => {
        const next = { ...prev }
        delete next[row.subscription_id]
        return next
      })
    } catch (err) {
      // 5xx 케이스 — FunctionsHttpError 또는 네트워크 에러
      // FunctionsHttpError의 context를 통해 body를 파싱 시도
      let body: Record<string, unknown> | null = null
      try {
        const ctx = (err as { context?: Response }).context
        if (ctx && typeof ctx.json === 'function') {
          body = await ctx.json()
        }
      } catch {
        // 무시
      }

      const reason = body?.reason as string | undefined
      const warning = body?.warning as string | undefined
      const message = (body?.message as string | undefined) ?? (err instanceof Error ? err.message : String(err))

      if (warning) {
        toast.error('결제는 성공했고 반영만 실패했습니다. ' + warning)
        setRowStatus((prev) => {
          const next = { ...prev }
          delete next[row.subscription_id]
          return next
        })
        return
      }

      if (reason === 'billing_payment_infra_error' || reason === 'billing_payment_fetch_error') {
        toast.error('결제 성공 여부를 확인하지 못했습니다. ' + message)
        setRowStatus((prev) => {
          const next = { ...prev }
          delete next[row.subscription_id]
          return next
        })
        return
      }

      // 기타 5xx
      console.error('[AdminBillingRetry] 재결제 오류:', err, body)
      toast.error('재결제 중 오류: ' + message)
      setRowStatus((prev) => {
        const next = { ...prev }
        delete next[row.subscription_id]
        return next
      })
    }
  }

  // 권한 없음
  if (!user || !ADMIN_USER_IDS.includes(user.id)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-gray-500">접근 권한이 없습니다.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <Helmet>
        <title>어드민 - 결제 실패 재시도</title>
      </Helmet>

      <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
        <CreditCard className="w-6 h-6" />
        결제 실패 재시도
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        past_due 상태인 구독 목록입니다. 재결제 버튼을 누르면 PortOne으로 실제 결제가 발생합니다.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      ) : loadError ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500 mb-4">{loadError}</p>
            <Button onClick={() => window.location.reload()}>다시 시도</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>past_due 구독 ({rows.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <p className="text-center text-gray-500 py-8">결제 실패 상태인 구독이 없습니다. 🎉</p>
            ) : (
              <div className="space-y-3">
                {rows.map((row) => {
                  const status = rowStatus[row.subscription_id] ?? { kind: 'idle' }
                  const retrying = status.kind === 'retrying'

                  return (
                    <div
                      key={row.subscription_id}
                      className={`border rounded-lg p-4 ${
                        status.kind === 'declined'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="space-y-1 min-w-0 flex-1">
                          <p className="font-medium truncate">
                            {row.nickname ?? '(닉네임 없음)'}{' '}
                            <span className="text-xs text-gray-400">{row.user_id}</span>
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">{row.plan_name ?? row.subscription_plan_id}</span>
                            {' · '}
                            <span>{formatAmount(row.amount)}</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            기간: {formatDate(row.period_start)} ~ {formatDate(row.period_end)}
                          </p>
                          {!row.has_billing_key && (
                            <p className="text-xs text-red-500 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              빌링키 없음
                            </p>
                          )}
                          {status.kind === 'declined' && (
                            <p className="text-xs text-yellow-700 mt-1">
                              마지막 거절: {status.pgMessage || status.message || status.errorType}
                            </p>
                          )}
                          {status.kind === 'precondition_failed' && (
                            <p className="text-xs text-gray-600 mt-1">사유: {status.reason}</p>
                          )}
                        </div>
                        <div className="flex items-center shrink-0">
                          <Button
                            size="sm"
                            onClick={() => handleRetry(row)}
                            disabled={retrying || !row.has_billing_key}
                            className="bg-brand-primary hover:bg-brand-primary/90"
                          >
                            {retrying ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                재결제 중...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-1" />
                                재결제
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
